import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { Check, Building2, Briefcase, Layers, Users, UserPlus, FolderPlus, ListTodo, ChevronRight, ChevronLeft, SkipForward, Loader2 } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { onboardingService } from '../services/onboarding'
import { hierarchyService } from '../services/hierarchy'
import { labelsService, IndustryPreset } from '../services/labels'
import { userInvitationsService } from '../services/userInvitations'
import { parseInviteEmails } from '../utils/inviteEmails'
import { useLabels } from '../contexts/LabelsContext'
import { useTranslation } from '../i18n/I18nContext'
import { apiClient } from '../services/api'
import { toast } from 'sonner'
import { track } from '../utils/analytics'
import { useCurrentOrgId } from '../contexts/AuthContext'

const STEPS = [
  { id: 1, label: 'Organization', icon: Building2, title: 'Name your organization', description: 'This appears across all dashboards and reports.' },
  { id: 2, label: 'Industry', icon: Briefcase, title: 'What kind of organization is this?', description: 'We use this to choose sensible vocabulary and defaults. You can change it later.' },
  { id: 3, label: 'Hierarchy', icon: Layers, title: 'Configure your hierarchy', description: 'Optional. Skip if your organization is flat — you can add structure later.' },
  { id: 4, label: 'Roles', icon: Users, title: 'Define your roles', description: 'Default roles are ready to use. Customize names and permissions anytime.' },
  { id: 5, label: 'Invite', icon: UserPlus, title: 'Invite your team', description: 'Add email addresses. They\'ll receive an invite link.' },
  { id: 6, label: 'First project', icon: FolderPlus, title: 'Create your first project', description: 'Start blank or pick a template.' },
  { id: 7, label: 'Tasks', icon: ListTodo, title: 'Assign your first tasks', description: 'Get your team moving from day one.' },
]

const DEFAULT_HIERARCHY = [
  { level_number: 1, display_name: 'Department', plural_name: 'Departments' },
  { level_number: 2, display_name: 'Team', plural_name: 'Teams' },
  { level_number: 3, display_name: 'Project', plural_name: 'Projects' },
]

interface HierarchyLevelDraft {
  level_number: number
  display_name: string
  plural_name: string
}

export default function OnboardingWizard() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { refresh: refreshLabels, setFromResponse } = useLabels()
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [orgName, setOrgName] = useState('')
  const [demoMode, setDemoMode] = useState(false)
  const [industries, setIndustries] = useState<IndustryPreset[]>([])
  const [industryKey, setIndustryKey] = useState<string>('generic')
  const [flatMode, setFlatMode] = useState(false)
  const [hierarchyLevels, setHierarchyLevels] = useState<HierarchyLevelDraft[]>(DEFAULT_HIERARCHY)
  const [inviteEmails, setInviteEmails] = useState('')
  const [projectName, setProjectName] = useState('')

  // The new owner's own organization (was hardcoded to 1, which 404'd for
  // every SaaS signup: rename, industry pack and progress all failed).
  const ORG_ID = useCurrentOrgId() ?? 1

  useEffect(() => {
    // Sprint 16 — both fetches were silent. Non-critical (defaults are
    // step 1 + empty industry list, both safe), but log to console so
    // dev tools surface the failure during debugging.
    onboardingService.getProgress(ORG_ID).then(p => {
      if (p.is_complete) navigate('/dashboard')
      else setStep(p.current_step)
    }).catch((err) => {
      console.warn('onboarding getProgress failed; staying at step 1:', err)
    })
    labelsService.getIndustries().then(setIndustries).catch((err) => {
      console.warn('onboarding getIndustries failed; dropdown will be empty:', err)
    })
  }, [navigate, ORG_ID])

  const canProceed = () => {
    if (step === 1) return orgName.trim().length > 0
    if (step === 2) return industryKey.trim().length > 0
    return true
  }

  async function advance() {
    setSaving(true)
    try {
      await onboardingService.completeStep(ORG_ID, step)

      // Step 1 — org rename. Sprint 16: was a silent .catch(() => {})
      // which meant a 500 here left the user with the default org name
      // and no clue. Now toasts on failure and aborts the step so the
      // user can retry instead of progressing in a half-broken state.
      if (step === 1 && orgName.trim()) {
        try {
          await apiClient.put(`/api/v1/organizations/${ORG_ID}`, { name: orgName.trim() })
        } catch {
          toast.error('Failed to save organisation name. Please try again.')
          return
        }
      }

      if (step === 2) {
        try {
          const resp = await labelsService.applyIndustry(ORG_ID, industryKey)
          setFromResponse(resp)
        } catch {
          // Fall through — labels stay generic; user can revisit in Settings.
        }
      }

      // Step 3 — hierarchy seeding. Sprint 16: was silent. A failure
      // here previously sent the user to /dashboard with no hierarchy
      // levels, which broke project creation. Now: toast + halt.
      if (step === 3 && !flatMode) {
        let failed = 0
        for (const level of hierarchyLevels) {
          try {
            await hierarchyService.createLevel(ORG_ID, { ...level, is_required: true, max_depth: null })
          } catch {
            failed += 1
          }
        }
        if (failed > 0) {
          toast.error(
            `Could not create ${failed} of ${hierarchyLevels.length} hierarchy levels. ` +
            `Check your permissions and try again, or pick the flat-mode option below.`,
          )
          return
        }
      }

      // Step 5 — send team invitations. Best-effort: a failed send (or an
      // unverified owner hitting the Sprint 30 403 gate) must NOT block the
      // user from finishing onboarding. We surface results via toasts and
      // fall through to the next step regardless.
      if (step === 5 && inviteEmails.trim()) {
        const { valid, invalid } = parseInviteEmails(inviteEmails)
        if (invalid.length > 0) {
          toast.error(t('invite.onboarding.invalid', {
            count: invalid.length, emails: invalid.join(', '),
          }))
        }
        let sent = 0
        let failed = 0
        let unverified = false
        let emailWarning: string | null = null
        for (const email of valid) {
          try {
            const created = await userInvitationsService.create({ email })
            if (created?.email_sent === false) {
              emailWarning = created.email_warning || null
              failed += 1
            } else {
              sent += 1
            }
          } catch (err) {
            const status = axios.isAxiosError(err) ? err.response?.status : undefined
            const code = axios.isAxiosError(err)
              ? (err.response?.data as { detail?: { code?: string } } | undefined)?.detail?.code
              : undefined
            if (status === 403 && code === 'email_unverified') {
              unverified = true
              break  // account-level block — no point trying the rest
            }
            failed += 1
          }
        }
        if (unverified) {
          toast.error(t('invite.unverified'))
        } else {
          if (sent > 0) toast.success(t('invite.onboarding.someSent', { count: sent }))
          if (emailWarning) toast.warning(emailWarning)
          else if (failed > 0) toast.error(t('invite.onboarding.someFailed', { count: failed }))
        }
      }

      if (step < STEPS.length) setStep(s => s + 1)
      else {
        await refreshLabels()
        track('onboarding_completed', { skipped_last_step: false })
        navigate('/dashboard')
      }
    } finally {
      setSaving(false)
    }
  }

  const skip = () => {
    // Sprint 16: completeStep tracking is best-effort analytics — losing
    // it doesn't block the user. Log to console so it's visible in dev
    // tools instead of being completely silent.
    onboardingService.completeStep(ORG_ID, step).catch((err) => {
      console.warn('onboarding step-tracking failed (non-blocking):', err)
    })
    if (step < STEPS.length) setStep(s => s + 1)
    else {
      track('onboarding_completed', { skipped_last_step: true })
      navigate('/dashboard')
    }
  }

  const finish = () => {
    track('onboarding_completed', { exited_at_step: step })
    navigate('/dashboard')
  }

  const currentStep = STEPS[step - 1]

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                      s.id < step
                        ? 'bg-primary border-primary text-primary-foreground'
                        : s.id === step
                        ? 'border-primary text-primary bg-background'
                        : 'border-muted text-muted-foreground bg-background'
                    }`}
                    aria-label={`Step ${s.id}: ${s.label}${s.id < step ? ' (complete)' : s.id === step ? ' (current)' : ''}`}
                  >
                    {s.id < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                  </div>
                  <span className={`text-xs mt-1 hidden sm:block ${s.id === step ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 transition-colors ${s.id < step ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Card */}
        <div className="rounded-xl border bg-card shadow-sm p-8">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-foreground">{currentStep.title}</h1>
            <p className="text-sm text-muted-foreground mt-1">{currentStep.description}</p>
          </div>

          {/* Step content */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5" htmlFor="org-name">
                  Organization name <span aria-hidden="true" className="text-red-500">*</span>
                </label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  placeholder="e.g. Acme Corp, City of Springfield, St. Mary's Hospital"
                  autoFocus
                  aria-required="true"
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={demoMode}
                  onChange={e => setDemoMode(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-muted-foreground">
                  Load example data so I can explore the product first — I'll wipe it when ready.
                </span>
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2" role="radiogroup" aria-label="Industry">
              {industries.length === 0 && (
                <p className="text-sm text-muted-foreground">Loading options…</p>
              )}
              {industries.map(opt => {
                const selected = industryKey === opt.key
                return (
                  <label
                    key={opt.key}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected ? 'border-primary bg-primary/5' : 'border-input bg-background hover:bg-muted/30'}`}
                  >
                    <input
                      type="radio"
                      name="industry"
                      value={opt.key}
                      checked={selected}
                      onChange={() => setIndustryKey(opt.key)}
                      className="mt-1"
                      aria-label={opt.name}
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground">{opt.name}</div>
                      <div className="text-xs text-muted-foreground">{opt.description}</div>
                    </div>
                  </label>
                )
              })}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={flatMode}
                  onChange={e => setFlatMode(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-foreground">
                  My organization is flat — skip departments and teams for now.
                </span>
              </label>
              {flatMode ? (
                <p className="text-sm text-muted-foreground">
                  You can add departments, teams, or any custom structure later from <strong>Admin → Settings</strong>.
                </p>
              ) : (
                <>
              <p className="text-sm text-muted-foreground">Drag to reorder. Rename to match your org's terminology.</p>
              {hierarchyLevels.map((level, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <span className="text-sm font-medium text-muted-foreground w-6">{i + 1}.</span>
                  <Input
                    value={level.display_name}
                    onChange={e => setHierarchyLevels(ls => ls.map((l, j) => j === i ? { ...l, display_name: e.target.value } : l))}
                    placeholder="e.g. Department"
                    className="flex-1"
                    aria-label={`Level ${i + 1} name`}
                  />
                  <Input
                    value={level.plural_name}
                    onChange={e => setHierarchyLevels(ls => ls.map((l, j) => j === i ? { ...l, plural_name: e.target.value } : l))}
                    placeholder="e.g. Departments"
                    className="w-40"
                    aria-label={`Level ${i + 1} plural name`}
                  />
                  {hierarchyLevels.length > 1 && (
                    <button
                      onClick={() => setHierarchyLevels(ls => ls.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-red-500 transition-colors text-sm"
                      aria-label={`Remove level ${i + 1}`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHierarchyLevels(ls => [...ls, { level_number: ls.length + 1, display_name: '', plural_name: '' }])}
              >
                + Add level
              </Button>
                </>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">
                Default roles are ready. You can rename them or add custom roles in <strong>Admin → Roles</strong> after setup.
              </p>
              {['Executive', 'Project Manager', 'Team Lead', 'Member'].map(role => (
                <div key={role} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">{role}</span>
                </div>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground block mb-1.5" htmlFor="invite-emails">
                Email addresses
              </label>
              <textarea
                id="invite-emails"
                value={inviteEmails}
                onChange={e => setInviteEmails(e.target.value)}
                placeholder="alice@company.com&#10;bob@company.com&#10;..."
                rows={5}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                aria-label="Email addresses to invite, one per line"
              />
              <p className="text-xs text-muted-foreground">One email per line. They'll receive an invite link via email.</p>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5" htmlFor="project-name">
                  Project name
                </label>
                <Input
                  id="project-name"
                  value={projectName}
                  onChange={e => setProjectName(e.target.value)}
                  placeholder="e.g. Q3 Platform Redesign"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Or pick a template after setup — go to <strong>Projects → Create</strong>.
              </p>
            </div>
          )}

          {step === 7 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-semibold text-foreground mb-2">You're live.</h2>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Your workspace is ready. Assign tasks to your team from the Projects view, or explore your dashboard.
              </p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t">
            <div>
              {step > 1 && step < STEPS.length && (
                <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} className="gap-1">
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {step < STEPS.length && step !== 1 && (
                <Button variant="ghost" size="sm" onClick={skip} className="gap-1 text-muted-foreground">
                  <SkipForward className="w-4 h-4" />
                  Skip
                </Button>
              )}
              {step < STEPS.length ? (
                <Button onClick={advance} disabled={saving || !canProceed()} className="gap-1">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {step === STEPS.length - 1 ? 'Create project' : 'Continue'}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button onClick={finish} className="gap-1">
                  Go to dashboard
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          You can revisit any of these settings in <strong>Admin → Settings</strong> at any time.
        </p>
      </div>
    </div>
  )
}
